import type { MediaItem } from './types.js';

export type ClockOffsets = Record<string, number>;

/** Shifts capture times for one camera only, in seconds (PRD 7.3). */
export function applyClockOffset(items: MediaItem[], offsets: ClockOffsets): MediaItem[] {
  return items.map((item) => {
    const offset = offsets[item.camera];
    if (!offset) return item;
    const shifted = new Date(new Date(item.capturedAt).getTime() + offset * 1000);
    return { ...item, capturedAt: isoWithSameOffset(item.capturedAt, shifted) };
  });
}

function isoWithSameOffset(original: string, date: Date): string {
  const match = /([+-]\d{2}:\d{2}|Z)$/.exec(original);
  const suffix = match?.[1] ?? 'Z';
  if (suffix === 'Z') return date.toISOString();
  const sign = suffix.startsWith('-') ? -1 : 1;
  const [h = '0', m = '0'] = suffix.slice(1).split(':');
  const minutes = sign * (Number(h) * 60 + Number(m));
  const local = new Date(date.getTime() + minutes * 60_000);
  return local.toISOString().replace('Z', suffix);
}

/**
 * Photos and clips in the order they happened. Ties break by camera then
 * filename so a re-run always produces the same order (US-02).
 */
export function sortItems(items: MediaItem[]): MediaItem[] {
  return [...items].sort((a, b) => {
    const ta = Date.parse(a.capturedAt);
    const tb = Date.parse(b.capturedAt);
    if (ta !== tb) return ta - tb;
    if (a.camera !== b.camera) return a.camera < b.camera ? -1 : 1;
    if (a.filename !== b.filename) return a.filename < b.filename ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function visible(items: MediaItem[]): MediaItem[] {
  return items.filter((item) => !item.hidden);
}
